import {
    type CSSProperties,
    type ReactNode,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type StepperStepEntry,
    Stepper,
    Button,
    Switch,
    Text
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Stepper";

const USAGE_CODE = `const [activeIndex, setActiveIndex] = useState(1);

<Stepper
    onStepPress={(index) => setActiveIndex(index)}
    accessibilityLabel="Checkout steps"
    activeIndex={activeIndex}
    steps={[
        {
            title: "Account"
        },
        {
            title: "Address"
        },
        {
            title: "Payment"
        }
    ]}
/>`;

const VERTICAL_LAST_INDEX = 2;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const FLOW_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    width: "100%",
    gap: 24
};

const ACTIONS_STYLE: CSSProperties = {
    justifyContent: "flex-end",
    display: "flex",
    gap: 8
};

const BUTTONS_STYLE: CSSProperties = {
    display: "flex",
    gap: 8
};

const COLUMN_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    width: "100%",
    gap: 12
};

const NARROW_STYLE: CSSProperties = {
    maxWidth: "100%",
    width: 360
};

const toSteps = (titles: Array<string>): Array<StepperStepEntry> => titles.map((title) => ({
    title
}));

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("stepperPage.orientationTitle"),
        id: "orientation"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("stepperPage.linearTitle"),
        id: "linear"
    },
    {
        title: t("stepperPage.compactTitle"),
        id: "compact"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const StepperPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        flowIndex,
        setFlowIndex
    ] = useState(1);

    const [
        verticalIndex,
        setVerticalIndex
    ] = useState(1);

    const [
        freeIndex,
        setFreeIndex
    ] = useState(1);

    const [
        isLinear,
        setIsLinear
    ] = useState(true);

    const [
        compactIndex,
        setCompactIndex
    ] = useState(2);

    const flowSteps = toSteps([
        t("sample.step1"),
        t("sample.step2"),
        t("sample.step3"),
        t("stepperPage.stepConfirm")
    ]);

    const lastIndex = flowSteps.length - 1;

    const handleFlowPress = (index: number) => setFlowIndex(index);

    const handleFlowBack = () => setFlowIndex(Math.max(0, flowIndex - 1));

    const handleFlowNext = () => setFlowIndex(Math.min(lastIndex, flowIndex + 1));

    const handleVerticalPress = (index: number) => setVerticalIndex(index);

    const handleVerticalBack = () => setVerticalIndex(Math.max(0, verticalIndex - 1));

    const handleVerticalNext = () => setVerticalIndex(Math.min(VERTICAL_LAST_INDEX, verticalIndex + 1));

    const handleFreePress = (index: number) => setFreeIndex(index);

    const handleLinearToggle = () => setIsLinear(!isLinear);

    const handleCompactPress = (index: number) => setCompactIndex(index);

    const renderPanel = (text: string) => <div
        style={COLUMN_STYLE}
    >
        <Text
            variant="bodyMediumSize"
            color="mid"
        >
            {text}
        </Text>
        <div
            style={BUTTONS_STYLE}
        >
            <Button
                isDisabled={verticalIndex === 0}
                title={t("stepperPage.back")}
                onPress={handleVerticalBack}
                variant="outline"
                type="neutral"
                size="small"
            />
            <Button
                isDisabled={verticalIndex === VERTICAL_LAST_INDEX}
                title={t("stepperPage.next")}
                onPress={handleVerticalNext}
                size="small"
            />
        </div>
    </div>;

    const verticalSteps: Array<StepperStepEntry> = [
        {
            content: renderPanel(t("stepperPage.contentAccount")),
            title: t("sample.step1")
        },
        {
            content: renderPanel(t("stepperPage.contentAddress")),
            title: t("sample.step2")
        },
        {
            content: renderPanel(t("stepperPage.contentPayment")),
            title: t("sample.step3")
        }
    ];

    const statusSteps: Array<StepperStepEntry> = [
        {
            title: t("sample.step1")
        },
        {
            title: t("sample.step2"),
            status: "skipped",
            isOptional: true
        },
        {
            description: t("stepperPage.cardMissing"),
            title: t("sample.step3"),
            status: "error"
        },
        {
            title: t("stepperPage.stepConfirm")
        },
        {
            description: t("stepperPage.lockedDesc"),
            title: t("stepperPage.stepReceipt"),
            status: "disabled"
        }
    ];

    const freeSteps: Array<StepperStepEntry> = [
        {
            title: t("sample.step1")
        },
        {
            title: t("sample.step2")
        },
        {
            title: t("sample.step3")
        },
        {
            title: t("stepperPage.stepConfirm"),
            status: "disabled"
        }
    ];

    const compactSteps: Array<StepperStepEntry> = [
        {
            title: t("sample.step1")
        },
        {
            title: t("sample.step2")
        },
        {
            title: t("sample.step3"),
            status: "error"
        },
        {
            title: t("stepperPage.stepConfirm")
        }
    ];

    const renderItem = (label: string, node: ReactNode) => <div
        style={COLUMN_STYLE}
        key={label}
    >
        <Text
            variant="bodySmallSize"
            color="low"
        >
            {label}
        </Text>
        {node}
    </div>;

    return <Article>
        <ComponentHeader
            lead={t("stepperPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("stepperPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("stepperPage.overviewCaption")}
            >
                <div
                    style={FLOW_STYLE}
                >
                    <Stepper
                        accessibilityLabel={t("stepperPage.label")}
                        onStepPress={handleFlowPress}
                        activeIndex={flowIndex}
                        steps={flowSteps}
                    />
                    <div
                        style={ACTIONS_STYLE}
                    >
                        <Button
                            title={t("stepperPage.back")}
                            isDisabled={flowIndex === 0}
                            onPress={handleFlowBack}
                            variant="outline"
                            type="neutral"
                        />
                        <Button
                            isDisabled={flowIndex === lastIndex}
                            title={t("stepperPage.next")}
                            onPress={handleFlowNext}
                        />
                    </div>
                </div>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("stepperPage.orientationTitle")}
            id="orientation"
        >
            <Paragraph>
                {t("stepperPage.orientationText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Stepper
                    accessibilityLabel={t("stepperPage.label")}
                    onStepPress={handleVerticalPress}
                    activeIndex={verticalIndex}
                    orientation="vertical"
                    steps={verticalSteps}
                />
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("stepperPage.sizesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => renderItem(`size="${size}"`, <Stepper
                        compactBehaviour="never"
                        steps={toSteps([
                            t("sample.step1"),
                            t("sample.step2"),
                            t("sample.step3")
                        ])}
                        activeIndex={1}
                        size={size}
                    />))
                }
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("stepperPage.statesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Stepper
                    orientation="vertical"
                    steps={statusSteps}
                    activeIndex={3}
                />
            </Preview>
        </Section>
        <Section
            title={t("stepperPage.linearTitle")}
            id="linear"
        >
            <Paragraph>
                {t("stepperPage.linearText")}
            </Paragraph>
            <Preview
                caption={t("stepperPage.linearCaption")}
            >
                <div
                    style={FLOW_STYLE}
                >
                    <Stepper
                        accessibilityLabel={t("stepperPage.label")}
                        onStepPress={handleFreePress}
                        compactBehaviour="never"
                        activeIndex={freeIndex}
                        isLinear={isLinear}
                        steps={freeSteps}
                    />
                    <Switch
                        title={t("stepperPage.linearSwitch")}
                        onPress={handleLinearToggle}
                        isChecked={isLinear}
                    />
                </div>
            </Preview>
        </Section>
        <Section
            title={t("stepperPage.compactTitle")}
            id="compact"
        >
            <Paragraph>
                {t("stepperPage.compactText")}
            </Paragraph>
            <Preview
                caption={t("stepperPage.compactCaption")}
            >
                <div
                    style={NARROW_STYLE}
                >
                    <Stepper
                        accessibilityLabel={t("stepperPage.label")}
                        onStepPress={handleCompactPress}
                        activeIndex={compactIndex}
                        steps={compactSteps}
                    />
                </div>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("stepperPage.a11yNav"),
                    t("stepperPage.a11yStep"),
                    t("stepperPage.a11yKeyboard"),
                    t("stepperPage.a11yStatus"),
                    t("stepperPage.a11yCompact"),
                    t("stepperPage.a11yMotion"),
                    t("stepperPage.a11yParts")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("stepperPage.propSteps"),
                        type: "Array<StepperStepEntry>",
                        defaultValue: "[]",
                        name: "steps"
                    },
                    {
                        description: t("stepperPage.propActiveIndex"),
                        name: "activeIndex",
                        defaultValue: "0",
                        type: "number"
                    },
                    {
                        type: "(index: number, step: StepperStepEntry) => void",
                        description: t("stepperPage.propOnStepPress"),
                        name: "onStepPress"
                    },
                    {
                        description: t("stepperPage.propOrientation"),
                        type: "\"horizontal\" | \"vertical\"",
                        defaultValue: "\"horizontal\"",
                        name: "orientation"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("stepperPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("stepperPage.propIsLinear"),
                        defaultValue: "true",
                        name: "isLinear",
                        type: "boolean"
                    },
                    {
                        description: t("stepperPage.propCompactBehaviour"),
                        type: "\"auto\" | \"never\"",
                        name: "compactBehaviour",
                        defaultValue: "\"auto\""
                    },
                    {
                        description: t("stepperPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("stepperPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("stepperPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("stepperPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("stepperPage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("stepperPage.entriesText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("stepperPage.entryTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("stepperPage.entryDescription"),
                        name: "description",
                        type: "string"
                    },
                    {
                        type: "\"upcoming\" | \"completed\" | \"skipped\" | \"error\" | \"disabled\"",
                        description: t("stepperPage.entryStatus"),
                        name: "status"
                    },
                    {
                        description: t("stepperPage.entryIsOptional"),
                        name: "isOptional",
                        type: "boolean"
                    },
                    {
                        description: t("stepperPage.entryContent"),
                        type: "ReactNode",
                        name: "content"
                    },
                    {
                        description: t("stepperPage.entryKey"),
                        type: "string",
                        name: "key"
                    }
                ]}
            />
            <Paragraph>
                {t("stepperPage.partsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("stepperPage.partItemIndex"),
                        name: "Stepper.Item · index",
                        type: "number"
                    },
                    {
                        description: t("stepperPage.partStepOnPress"),
                        name: "Stepper.Step · onPress",
                        type: "() => void"
                    },
                    {
                        description: t("stepperPage.partConnectorIsDone"),
                        name: "Stepper.Connector · isDone",
                        type: "boolean"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default StepperPage;
